import React, {useRef, useEffect, useState, useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useMotionValueEvent, useReducedMotion} from 'framer-motion'
import {Box} from '@chakra-ui/react3'
import {debounce} from 'lodash'
import DisImage from '../../components/dis-image'

import useOcapiSite from '../../../hooks/useOcapiSite'
import {getSharedLibraryAssetUrl} from '../../../utils/url'
import {getDisBaseUrl} from '../../../utils/dis-utils'
import useVisualViewportSize from '../../../hooks/use-visual-viewport'
import useIntersectionObserver from '../../../hooks/use-intersection-observer'
import {isServer} from '../../../utils/utils'

const WIDTHS = {
    'product-below-copy': {
        base: '100vw',
        s: '630px'
    },
    'side-by-side': {
        base: '100vw',
        s: '50vw',
        l: '840px'
    }
}

/**
 * Generates an image filename based on prefix, extension, and index
 * @param {string} prefix - The prefix for the image filename (e.g., "frame-")
 * @param {string} extension - The file extension without dot (e.g., "jpg", "png")
 * @param {number} index - The frame index number (1-based)
 * @returns {string} The complete filename (e.g., "frame-1.jpg")
 * @example
 * getImageName("product-", "png", 5) // returns "product-5.png"
 */

/**
 * Maps scroll progress (0-1) to frame number (1-based indexing)
 * @param {number} scrollProgress - Scroll progress value between 0 and 1
 * @param {number} totalFrames - Total number of frames in the animation sequence
 * @returns {number} Frame number between 1 and totalFrames
 * @example
 * calculateFrameFromProgress(0, 29)    // returns 1 (first frame)
 * calculateFrameFromProgress(0.5, 29)  // returns 15 (middle frame)
 * calculateFrameFromProgress(1, 29)    // returns 29 (last frame)
 */
const calculateFrameFromProgress = (scrollProgress, totalFrames) => {
    // get how should be the in the progress for the current amount of frames
    const gap = 1 / totalFrames
    // Floor the division to get discrete frame steps, add 1 for 1-based indexing,
    const actualFrame = Math.floor(scrollProgress / gap) + 1
    // then clamp between 1 and imageCount to ensure valid frame bounds
    const newFrame = Math.max(1, Math.min(totalFrames, actualFrame))

    return newFrame
}

const getBestExtension = (imageExtension) => {
    if (isServer) return imageExtension

    const supportsAvif = CSS.supports(
        'background-image',
        'url(data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAG1pZg==)'
    )
    if (supportsAvif) {
        return 'avif'
    }

    const supportsWebp = CSS.supports(
        'background-image',
        'url(data:image/webp;base64,UklGRiQAAABXRUJQVlA4ICQAAACwAQCdASoBAAEADsD+JaQAA3AAAAAA)'
    )
    if (supportsWebp) {
        return 'webp'
    }

    return imageExtension
}

// this hook is just an encapsulation at component level not intended to use outside of it
// delay the start of the images load until the container is 100vh below the fold
const useStartLoad = (containerRef) => {
    const {height: viewPortHeight} = useVisualViewportSize()

    const [, isIntersecting] = useIntersectionObserver(
        {
            threshold: 0.01,
            rootMargin: `0px 0px ${viewPortHeight}px 0px`,
            useOnce: true
        },
        containerRef
    )

    return isIntersecting
}

/**
 * Generates prioritized loading order for image frames
 * Loads in order: last frame, first frame, then binary splits of remaining gaps
 * @param {number} totalFrames - Total number of frames in the animation sequence
 * @returns {number[]} Array of frame numbers in priority order
 * @example
 * getPrioritizedLoadingOrder(29)
 * // returns [29, 1, 15, 8, 22, 4, 12, 19, 26, ...]
 */
const getPrioritizedLoadingOrder = (totalFrames) => {
    const order = []
    const queue = []

    // Start with last frame
    order.push(totalFrames)

    // Add first frame only if different from last
    if (totalFrames <= 1) {
        return order
    }

    order.push(1)
    queue.push([1, totalFrames])

    while (queue.length > 0) {
        const [start, end] = queue.shift()
        const gap = end - start - 1

        if (gap < 1) continue

        // Find midpoint of the gap
        const midpoint = Math.floor((start + end) / 2)

        order.push(midpoint)
        queue.push([midpoint, end])
        queue.push([start, midpoint])
    }

    return order
}

/**
 * Finds the closest loaded frame to the requested frame number
 * @param {number} targetFrame - The desired frame number
 * @returns {number|null} The closest loaded frame number, or null if no frames loaded
 */
const findClosestLoadedFrame = (targetFrame, loadedFrames) => {
    const loaded = Array.from(loadedFrames).sort((a, b) => a - b)

    if (loaded.length === 0) return null
    if (loaded.includes(targetFrame)) return targetFrame

    // Find the closest frame by comparing distances
    let closest = loaded[0]
    let minDistance = Math.abs(targetFrame - closest)

    for (const frame of loaded) {
        const distance = Math.abs(targetFrame - frame)
        if (distance < minDistance) {
            minDistance = distance
            closest = frame
        }
    }

    return closest
}

/**
 * ProductAnimated - Scroll-based Image Sequence Animation Component
 *
 * Creates a smooth scroll-triggered animation by displaying sequential images
 * on an HTML5 Canvas element. As the user scrolls, different frames are shown
 * to create a cinematic animation effect. Images are preloaded and cached for
 * smooth playback performance.
 *
 * ## Features:
 * - Scroll-based frame animation using Framer Motion
 * - Image preloading and caching for smooth performance
 * - SSR-compatible with CLS prevention
 * - High-DPI display support
 * - Responsive canvas with aspect ratio preservation
 * - Accessibility
 * - lazy loading images
 *
 * @typedef {Object} ProductAnimatedProps
 * @property {string} imagePrefix - Prefix for image filenames
 * @property {number} imageCount - Total number of frames
 * @property {string} imageExtension - File extension for images
 * @property {number} [width=600] - Canvas width in pixels
 * @property {number} [height=600] - Canvas height in pixels
 *
 * @param {ProductAnimatedProps} props - Component props
 * @returns {React.ReactElement}
 */
function ProductAnimated({
    imagePrefix,
    scrollYProgress,
    imageCount,
    imageExtension,
    alt,
    layout,
    loading = 'lazy'
}) {
    const shouldReduceMotion = useReducedMotion()
    const {sharedLibraryStatic, sharedLibraryDis} = useOcapiSite()

    const getImageUrl = useCallback(
        (index, canvasWidth) => {
            const assetSrc = `${imagePrefix}${index}.${getBestExtension(imageExtension)}`
            const imageUrl = getSharedLibraryAssetUrl(assetSrc, sharedLibraryStatic)
            return `${getDisBaseUrl(
                imageUrl,
                sharedLibraryDis
            )}?sfrm=${imageExtension}&q=50&sw=${canvasWidth}`
        },
        [imagePrefix, sharedLibraryDis, sharedLibraryStatic, imageExtension]
    )

    const canvasRef = useRef(null)
    const containerRef = useRef(null)
    const imageCache = useRef(new Map())

    const [framesLoaded, setFramesLoaded] = useState([])
    const emptyFrames = framesLoaded.length === 0

    const startLoad = useStartLoad(containerRef)

    const drawFrame = useCallback((frameNumber) => {
        const canvas = canvasRef.current
        const container = containerRef.current
        if (!canvas || !container) return

        const cachedImage = imageCache.current.get(frameNumber)
        if (cachedImage && cachedImage.complete) {
            const context = canvas.getContext('2d')
            const rect = canvas.getBoundingClientRect()

            requestAnimationFrame(() => {
                const containerRect = container.getBoundingClientRect()
                // Set actual canvas dimensions (internal resolution)
                canvas.width = containerRect.width
                canvas.height = containerRect.height

                // Calculate scale to fit image within canvas while maintaining aspect ratio
                const imageAspect = cachedImage.width / cachedImage.height
                const canvasAspect = rect.width / rect.height

                let drawWidth, drawHeight
                if (imageAspect > canvasAspect) {
                    // Image is wider - fit to canvas width
                    drawWidth = rect.width
                    drawHeight = rect.width / imageAspect
                } else {
                    // Image is taller - fit to canvas height
                    drawHeight = rect.height
                    drawWidth = rect.height * imageAspect
                }

                // Center the image
                const x = (rect.width - drawWidth) / 2
                const y = (rect.height - drawHeight) / 2

                context.drawImage(cachedImage, x, y, drawWidth, drawHeight)
            })
        }
    }, [])

    const updateFrame = useCallback(
        (value) => {
            if (emptyFrames) return

            // Draw the frame directly instead of using state
            const newFrame = calculateFrameFromProgress(value, imageCount)
            if (framesLoaded.length === imageCount) {
                drawFrame(newFrame)
            } else {
                const closestFrame = findClosestLoadedFrame(newFrame, framesLoaded)
                drawFrame(closestFrame)
            }
        },
        [drawFrame, imageCount, emptyFrames, framesLoaded]
    )

    // Debounce scroll updates to ~60fps for better performance
    // This reduces main thread work during scroll and prevents frame skipping
    const debouncedUpdateFrame = useMemo(
        () => debounce(updateFrame, 16, {leading: true, trailing: true, maxWait: 16}),
        [updateFrame]
    )

    useMotionValueEvent(
        scrollYProgress,
        'change',
        !shouldReduceMotion ? debouncedUpdateFrame : () => {}
    )

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            debouncedUpdateFrame.cancel()
        }
    }, [debouncedUpdateFrame])

    // preload images
    useEffect(() => {
        let cancelled = false
        if (canvasRef.current === null || shouldReduceMotion || (loading === 'lazy' && !startLoad))
            return

        const cache = imageCache.current
        const canvasWidth = canvasRef.current.getBoundingClientRect().width

        const loadImage = (frameNumber) =>
            new Promise((res, rej) => {
                const img = new Image()
                img.onload = () => {
                    cache.set(frameNumber, img)
                    res()
                }

                img.onerror = (err) => {
                    console.error(`Failed to load image frame ${frameNumber}`)
                    console.error(err)
                    rej()
                }

                if (cancelled) res()

                img.src = getImageUrl(frameNumber, canvasWidth)
            })

        getPrioritizedLoadingOrder(imageCount).map((frameNumber) =>
            loadImage(frameNumber).then(() => {
                if (!cancelled) {
                    setFramesLoaded((prev) => [...prev, frameNumber])
                }
            })
        )

        return () => {
            cancelled = true
            cache.clear()
        }
    }, [
        imageCount,
        imageExtension,
        imagePrefix,
        getImageUrl,
        shouldReduceMotion,
        startLoad,
        loading
    ])

    useEffect(() => {
        if (!emptyFrames) {
            drawFrame(findClosestLoadedFrame(1, framesLoaded))
        }
    }, [drawFrame, emptyFrames, framesLoaded])

    return (
        <Box
            ref={containerRef}
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            position="relative"
        >
            <DisImage
                src={getImageUrl(shouldReduceMotion ? imageCount : 1)}
                alt={alt}
                width="100%"
                height="100%"
                position="absolute"
                objectFit="contain"
                top={0}
                left={0}
                opacity={emptyFrames ? 1 : 0}
                aria-hidden={!emptyFrames}
                transition="opacity 0.3s ease"
                loading="lazy"
                fetchpriority="auto"
                widths={WIDTHS[layout]}
            />
            <canvas
                aria-label={alt}
                role="img"
                ref={canvasRef}
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    margin: '0 auto',
                    opacity: emptyFrames ? 0 : 1
                }}
            />
        </Box>
    )
}

ProductAnimated.propTypes = {
    imagePrefix: PropTypes.string.isRequired,
    imageCount: PropTypes.number.isRequired,
    imageExtension: PropTypes.string.isRequired,
    imageWidth: PropTypes.number,
    imageHeight: PropTypes.number,
    containerHeight: PropTypes.object,
    scrollYProgress: PropTypes.object,
    alt: PropTypes.string.isRequired,
    layout: PropTypes.string,
    loading: PropTypes.oneOf(['lazy', 'eager'])
}

// this named export allow us to test this calculation on isolation without all the
// complexities of the scroll mocking
export {calculateFrameFromProgress}
export default ProductAnimated
